import util from './util';
import _ from 'lodash';
import Moment from 'moment-timezone';
import { MEASUREMENT_SYSTEM, MEASUREMENT_UNITS } from '@/models/app';
import type { CustomLocationType, LocationDataType } from '@/models/address';
import type { Ride, RideInfoItem, RideEstimate, RidePrefilledAddresses } from '@/models/ride';
import type { RideStop } from '@/models/rideOrdering';
import type { ScheduledRide, ExtendedScheduledRide } from '@/models/scheduledRide';
import { commonDateTimeFormat, dateTimeFormat, milesExchange } from '@/constants/app';
import { repeatWeeks } from '@/constants/ride';
import { formatEstimateRange } from './formatEstimateRange';

export { formatEstimateRange };

const trim = (value: string | null | undefined) => (value ?? '').trim();

const getStopStreetAddress = (stop: CustomLocationType) =>
  stop.type ? `${trim(stop.streetAddress)} & ${trim(stop.streetAddress1)}` : trim(stop.streetAddress);

export const buildDestinationStops = (stops?: CustomLocationType[]): RideStop[] => {
  if (!stops || stops.length === 0) return [];

  const result: RideStop[] = [];
  for (const s of stops) {
    const streetAddress = getStopStreetAddress(s);
    if (!streetAddress) continue;
    if (s.latitude == null || s.longitude == null) continue;

    const stop: RideStop = {
      streetAddress,
      city: trim(s.city),
      state: trim(s.state),
      zip: trim(s.zip),
      lat: Number(s.latitude),
      lng: Number(s.longitude),
    };
    if (s.type) stop.type = 'intersection';
    if (s.googlePlaceId) stop.googlePlaceId = s.googlePlaceId;
    result.push(stop);
  }
  return result;
};

/** True when a Venue Entrance / Side (not Original Address / home pin) is selected. */
export const isVenueEntranceSelected = (address?: { venueId?: string | null; zoneId?: string | null; locationId?: string | null }) =>
  Boolean(address?.venueId || address?.zoneId || address?.locationId);

/** A prefilled pickup / drop-off (IVR or landing page) in the shape the ordering and registration forms use */
const getPrefilledLocation = (address?: RidePrefilledAddresses['pickup'] | null): CustomLocationType => ({
  name: address?.name ?? '',
  streetAddress: address?.streetAddress ?? '',
  streetAddress1: '',
  city: address?.city ?? '',
  state: address?.state ?? '',
  zip: address?.zip ?? '',
  latitude: address?.latitude ?? null,
  longitude: address?.longitude ?? null,
  googlePlaceId: address?.placeId ?? null,
  type: false,
});

const makeEstimationPostData = (data: any, startStreetAddress: string, endStreetAddress: string, isAdditionalInfo: boolean) => {
  let postData: any = {
    phoneNumber: util.isPhoneNumberValid(data.phoneNumber) ? data.phoneNumber : '',
    riderPhone: util.isPhoneNumberValid(data.riderPhone) ? data.riderPhone : '',
    rideType: data.typeOfCar,
    partnerRidePurpose: data.partnerRidePurpose,
    startAddress: startStreetAddress,
    startCity: data.startAddress.city,
    startState: data.startAddress.state,
    startZip: data.startAddress.zip,
    startName: data.startAddress.name,
    startCoors: {
      lat: data.startAddress.latitude,
      lng: data.startAddress.longitude,
    },
    endAddress: endStreetAddress,
    endCity: data.endAddress.city,
    endState: data.endAddress.state,
    endZip: data.endAddress.zip,
    endName: data.endAddress.name,
    minutesToAutoRetryRequest: data.minutesToAutoRetryRequest ? Number(data.minutesToAutoRetryRequest) : 0,
    minutesToAutoRetryRequestType: data.minutesToAutoRetryRequestType,
    rideDateLocal: data.rideDateLocal,
    isDoNotApplySubsidy: data.rideType?.isDoNotApplySubsidy ? 1 : 0,
    isDoNotApplyExpiringCredits: data.rideType?.isDoNotApplyExpiringCredits ? 1 : 0,
  };

  // Only send Uber place ids when a Venue Entrance / Side is selected.
  // Original Address / home pin must go as lat/lng only so Uber does not snap to a nearby place.
  if (isVenueEntranceSelected(data.startAddress)) {
    postData.startLocationId = data.startAddress.locationId;
    postData.startZoneId = data.startAddress.zoneId;
    postData.startVenueId = data.startAddress.venueId;
    if (data.venuesInstruction) {
      postData.venuesPlaceInstructions = data.venuesInstruction;
    }
  }

  postData.noteToDriver = '';
  if (data.pickupDropoffNotes) {
    postData.noteToDriver = `${data.pickupDropoffNotes}\n`;
  }
  if (data.noteToDriver) {
    postData.noteToDriver += prefixNotes(data.noteToDriver);
  }

  if (data.isBusinessAccount) {
    postData = {
      ...postData,
      customRiderName: data.customRiderName,
    };
  }

  if (data.isMedicalTrip) {
    postData.isMedicalTrip = data.isMedicalTrip;
  }

  if (isAdditionalInfo) {
    postData = {
      ...postData,
      operatorNote: data.operatorNote,
      reason: data.reason,
      notesForLP: data.notesForLP,
      isEmergency: data.rideType.isEmergency ? 1 : 0,
      emergencyReason: data.rideType.isEmergency
        ? `${data.rideType.emergencyReason}\n\nEmergency ride was approved by ${data.rideType.approvedNameForEmergencyRide}`
        : null,
      overrideBadDriver: data.rideType.badDrivers ? 1 : 0,
      overrideBigCar: data.rideType.overrideBigCar ? 1 : 0,
      overrideSmallCar: data.rideType.overrideSmallCar ? 1 : 0,
      overrideBannedCar: data.rideType.overrideBannedCar ? 1 : 0,
    };
  }

  const destinationStops = buildDestinationStops(data.stops);
  if (destinationStops.length > 0) {
    postData.destinations = { stops: destinationStops };
  }

  return postData;
};

export const prefixNotes = (notes: string) =>
  notes
    ?.split('\n')
    .map((l) => l?.trim())
    .filter(Boolean)
    .map((l) => {
      if (/^\p{Extended_Pictographic}/u.test(l)) {
        // already starts with any emoji
        return l;
      } else {
        return '📝 ' + l;
      }
    })
    .join('\n');

const parseEstimationRide = ({ estimationData }: { estimationData: any }) => {
  let estimationRide: any = {};
  const data = estimationData.options ? estimationData.options[0] : estimationData;

  if (!data) {
    estimationRide.etaText = 'No drivers, try another vendor';
    estimationRide.hasNoDrivers = true;
    return estimationRide;
  }

  estimationRide = {
    ...data,
    units: data.units?.toUpperCase(),
    riderName: estimationData.firstName ? estimationData.firstName + ' ' + estimationData.lastName : 'Price Estimate',
    credits: data.rideCredits,
    callRideFromEstimateLink: data.callRideFromEstimateLink,
    etaText: _.isNil(data.eta) ? 'No drivers, try another vendor' : (data.approximateEta || data.eta) + ' min(s)',
    warningMessage: data.warningMessage || data.message,
    minutesToAutoRetryRequest: data.minutesToAutoRetryRequest ? `${data.minutesToAutoRetryRequest} minutes` : '',
    options: estimationData.options,
  };

  if (data.surgeFactor) {
    estimationRide.operatorConfirmedSurgePriceWithCustomer = false;
  }

  if (data.duration && data.distance && data.lowEstimate && data.highEstimate) {
    estimationRide.estimate = formatEstimateRange(estimationRide.lowEstimate, estimationRide.highEstimate);
    estimationRide.vendorEstimate =
      '$' +
      (Number(data.lowEstimate) * (data.isRoundTrip ? 2 : 1)).toFixed(2) +
      '-' +
      (Number(data.highEstimate) * (data.isRoundTrip ? 2 : 1)).toFixed(2);

    const medicalBaseFare = 11.95;
    const medicalDurationEstimate = 0.32 * Number(data.duration) * (data.isRoundTrip ? 2 : 1);
    const medicalDistanceEstimate = 0.68 * Number(data.distance) * (data.isRoundTrip ? 2 : 1);
    const medicalAppointmentEstimate = data.isRoundTrip ? 0.32 * Number(data.appointment) : 0;

    estimationRide.medicalEstimate = `$${(
      medicalBaseFare +
      medicalDurationEstimate +
      medicalDistanceEstimate +
      medicalAppointmentEstimate
    ).toFixed(2)}`;

    if (estimationRide.surgeFactor) {
      estimationRide.standardLowEstimate = (Number(data.lowEstimate) / Number(estimationRide.surgeFactor)).toFixed(2);
      estimationRide.standardHighEstimate = (Number(data.highEstimate) / Number(estimationRide.surgeFactor)).toFixed(2);
      estimationRide.standardEstimate = formatEstimateRange(estimationRide.standardLowEstimate, estimationRide.standardHighEstimate);
    }
  }

  return estimationRide;
};

const hasEstimateOptions = (apiResponse: any) => Boolean(apiResponse?.options?.length || apiResponse?.allPartnerEstimates?.length);

const getNoEstimateOptionsMessage = (apiResponse: any): string =>
  apiResponse?.warnings?.join('\n') || 'No ride options are currently available for this route. Please try again in a few minutes.';

const parseEstimationRideMulti = ({ apiResponse, isRoundTrip }: { apiResponse: any; isRoundTrip: boolean }): RideEstimate[] => {
  const { options, isAutoTip, firstName, lastName, minutesToAutoRetryRequest } = apiResponse;

  if (!options) {
    return [];
  }

  const rideEstimates: RideEstimate[] = [];

  for (const option of options) {
    const {
      rideType,
      surgeFactor,
      eta,
      duration,
      distance,
      lowEstimate,
      rideCostId,
      highEstimate,
      rideCredits,
      partnerSubsidy,
      partnerSubsidyEstimate,
      expiringCreditInfo,
      isNoReturnTrip,
      returnTripEtaMins,
      warningMessage,
      message,
      approximateEta,
      lowAutoTipAmount,
      highAutoTipAmount,
      perMile,
      perMinute,
      baseFare,
      minimumFare,
      operatingFee,
      isDefaultOption,
      providerName = '',
    } = option;

    const callRideFromEstimateLink = option.callRideFromEstimateLink;

    const appointment = apiResponse.appointment ?? option.appointment;

    let estimate: string | undefined;
    let vendorEstimate: string | undefined;
    let medicalEstimate: string | undefined;
    let standardLowEstimate: string | undefined;
    let standardHighEstimate: string | undefined;
    let standardEstimate: string | undefined;

    if (duration && distance && lowEstimate && highEstimate) {
      estimate = formatEstimateRange(lowEstimate, highEstimate);
      vendorEstimate =
        '$' + (Number(lowEstimate) * (isRoundTrip ? 2 : 1)).toFixed(2) + '-' + (Number(highEstimate) * (isRoundTrip ? 2 : 1)).toFixed(2);

      const medicalBaseFare = 11.95;
      const medicalDurationEstimate = 0.32 * Number(duration) * (isRoundTrip ? 2 : 1);
      const medicalDistanceEstimate = 0.68 * Number(distance) * (isRoundTrip ? 2 : 1);
      const medicalAppointmentEstimate = isRoundTrip ? 0.32 * Number(appointment) : 0;

      medicalEstimate = `$${(medicalBaseFare + medicalDurationEstimate + medicalDistanceEstimate + medicalAppointmentEstimate).toFixed(2)}`;

      if (surgeFactor) {
        standardLowEstimate = (Number(lowEstimate) / Number(surgeFactor)).toFixed(2);
        standardHighEstimate = (Number(highEstimate) / Number(surgeFactor)).toFixed(2);
        standardEstimate = formatEstimateRange(standardLowEstimate, standardHighEstimate);
      }
    }

    rideEstimates.push({
      firstName,
      lastName,
      rideType,
      providerName,
      surgeFactor,
      eta,
      duration,
      distance,
      lowEstimate,
      highEstimate,
      rideCostId,

      partnerSubsidy,
      partnerSubsidyEstimate,
      expiringCreditInfo,
      isNoReturnTrip,
      returnTripEtaMins,
      estimate,
      isAutoTip,
      lowAutoTipAmount,
      highAutoTipAmount,

      riderName: firstName ? firstName + ' ' + lastName : 'Price Estimate',
      credits: rideCredits,
      callRideFromEstimateLink,
      etaText: _.isNil(eta) ? 'No drivers, try another vendor' : (approximateEta || eta) + ' min(s)',
      warningMessage: warningMessage || message,
      minutesToAutoRetryRequest: minutesToAutoRetryRequest ? `${minutesToAutoRetryRequest} minutes` : '',

      vendorEstimate,
      medicalEstimate,
      standardHighEstimate,
      standardLowEstimate,
      standardEstimate,

      perMile,
      perMinute,
      baseFare,
      minimumFare,
      operatingFee,

      isDefaultOption,
    });
  }

  return rideEstimates;
};

const getRideSchedulerData = (data: any) => {
  const scheduleKind = data.rideMultiple
    ? 'multi'
    : data.scheduledAppointmentDate
      ? 'appointment'
      : data.isWillCall
        ? 'willcall'
        : 'single';

  const rideScheduler = {
    id: data.id,
    phoneNumber: data.callerPhoneNumber,
    riderPhone: data.riderPhone,
    callToConfirmSurge: data.callToConfirmSurge ? 'yes' : 'no',
    rideName: data.rideName,
    rideType: data.rideType,
    serviceType: data.serviceType,
    rideMultiple: data.rideMultiple,
    isWillCall: data.isWillCall,
    pickupDate: new Date(`${data.pickupDate} 00:00:00`),
    pickupTime: util.convertTime(data.pickupTime || ''),
    repeatEveryXWeek: data.repeatEveryXWeek || 1,
    day0: data.day0,
    day1: data.day1,
    day2: data.day2,
    day3: data.day3,
    day4: data.day4,
    day5: data.day5,
    day6: data.day6,
    endOn: data.endOn,
    endOnType: 'never',
    endAfterXTimes: data.endAfterXTimes,
    startAddress: {
      streetAddress: data.streetAddress1,
      streetAddress1: '',
      city: data.city1,
      state: data.state1,
      zip: data.zip1,
      name: data.locationName1,
      latitude: data.lat1,
      longitude: data.long1,
      type: false,
      locationId: data.locationId,
      zoneId: data.zoneId,
      venueId: data.venueId,
    },
    endAddress: {
      streetAddress: data.streetAddress2,
      streetAddress1: '',
      city: data.city2,
      state: data.state2,
      zip: data.zip2,
      name: data.locationName2,
      latitude: data.lat2,
      longitude: data.long2,
      type: false,
    },
    noteToDriver: data.noteToDriver,
    isNoNoteToDriver: data.isNoNoteToDriver || false,
    notesForLP: data.notesForLP,
    timeZone: data.timeZone,
    isDoNotApplySubsidy: data.isDoNotApplySubsidy,
    isDoNotApplyExpiringCredits: data.isDoNotApplyExpiringCredits,
    // isGoGoMedical: data.isGoGoMedical,
    isGoGoMedical: false,
    minutesToAutoRetryRequest: data.minutesToAutoRetryRequest ?? '',
    minutesBeforeCustomReminder: data.minutesBeforeCustomReminder ?? '',
    minutesAfterCreationCustomReminder: data.minutesAfterCreationCustomReminder ?? '',
    overrideBigCar: data.overrideBigCar,
    overrideSmallCar: data.overrideSmallCar,
    overrideBannedCar: data.overrideBannedCar,
    overrideBadDriver: data.overrideBadDriver,
    isExpandVehicleSearch: data.overrideBigCar && data.overrideSmallCar && data.overrideBannedCar ? true : false,
    isExpandDriverSearch: data.overrideBadDriver ? true : false,
    partnerId: data.partnerId,

    // for STA WAV 3rd party - B811 Medical Transportation Service
    gender: data.gender,
    levelOfService: data.levelOfService,
    hasOwnWheelchair: data.hasOwnWheelchair,
    specialInstructions: data.specialInstructions,
    numberOfCompanions: data.numberOfCompanions,
    appointmentType: data.appointmentType,
    appointmentTime: data.appointmentTime ? util.convertTime(data.appointmentTime) : null,
    returnTime: data.returnTime ? util.convertTime(data.returnTime) : null,
    locationName1: data.locationName1,
    stairStepsForPU: data.stairStepsForPU,
    locationName2: data.locationName2,
    stairStepsForDO: data.stairStepsForDO,
    dropOffPhoneNumber: data.dropOffPhoneNumber,
    scheduleKind,
    scheduledAppointmentDate: data.scheduledAppointmentDate ? new Date(`${data.pickupDate} 00:00:00`) : null,
    scheduledAppointmentTime: data.scheduledAppointmentTime ? util.convertTime(data.scheduledAppointmentTime || '') : null,
    scheduledAppointmentBufferMinutes: data.scheduledAppointmentBufferMinutes ?? 0,
    nextPickupDate: null,
  };

  if (rideScheduler.endOn && rideScheduler.endAfterXTimes) {
    rideScheduler.endOnType = 'never';
  } else if (!rideScheduler.endOn && rideScheduler.endAfterXTimes) {
    rideScheduler.endOnType = 'after';
  } else if (rideScheduler.endOn) {
    rideScheduler.endOnType = 'on';
  }

  const nextPickupTime = data.nextPickupTimeString && Moment(data.nextPickupTimeString);

  if (rideScheduler.rideMultiple && nextPickupTime?.isAfter(Moment())) {
    rideScheduler.nextPickupDate = nextPickupTime.format('YYYY-MM-DD');
  }

  return rideScheduler;
};

const buildIntermediateStopsDetail = (destinations: Ride['destinations']): { list: string[] } => {
  const stops = destinations?.stops;
  if (!stops?.length) {
    return { list: ['<span class="text-muted">None</span>'] };
  }

  const list = stops.map((stop, index) => {
    const parts = [stop.streetAddress, stop.city, stop.state, stop.zip].filter(Boolean);
    const address = parts.join(', ').replace(/\s+,/g, ',');
    return `<b>Stop ${index + 1}</b><br />${address || '—'}<br />Lat: ${stop.lat}, Lng: ${stop.lng}`;
  });

  return { list };
};

const formatDeliveryDetailValue = (key: string, value: unknown): string => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (key.toLowerCase().endsWith('cents') && typeof value === 'number') {
    return `$${(value / 100).toFixed(2)}`;
  }

  if (!value) {
    return '—';
  }

  return String(value);
};

const buildDeliveryDetailsList = (deliveryDetails: Record<string, unknown> | null | undefined): { list: string[] } => {
  if (!deliveryDetails || typeof deliveryDetails !== 'object') {
    return { list: ['<span class="text-muted">None</span>'] };
  }

  const list: string[] = [];

  for (const [key, value] of Object.entries(deliveryDetails)) {
    if (key === 'requirements') {
      continue;
    }
    list.push(`<b>${_.startCase(key)}</b>: ${formatDeliveryDetailValue(key, value)}`);
  }

  const requirements = Object.entries(deliveryDetails['requirements'])
    .map(([reqKey, reqValue]) => `${_.startCase(reqKey)}: ${formatDeliveryDetailValue(reqKey, reqValue)}`)
    .join(', ');
  list.push(`<b>Requirements</b>: ${requirements || '—'}`);

  if (!list.length) {
    return { list: ['<span class="text-muted">None</span>'] };
  }

  return { list };
};

const formatRideData = (ride: any, isDownload: boolean = false, timeZone?: string) => {
  ride.key = `${ride.id || ride.requestId}`;
  ride.originalCreatedAt = ride.createdAt;
  ride.vendor = _.startCase(ride.vendor || '');
  ride = _.merge({ customerName: util.getFullName(ride.firstName, ride.lastName) }, ride); // Move to the top

  if (isDownload) {
    for (const key of Object.keys(ride)) {
      if (typeof ride[key] === 'string') {
        ride[key] = ride[key].replace(/\#/g, ''); //remove # character in the record to prevent cuts off csv
      }
    }

    ride = {
      ...ride,
      ...formatRideDateTimes(ride, timeZone || ride.timeZone, 'M/DD/YYYY HH:mm'),
    };

    return ride;
  }

  const isOrderdByCustomer = ['user', 'customer', 'family'].includes(ride.orderedBy);
  const rideOrderedFrom = ride.orderedAtUrl
    ? isOrderdByCustomer
      ? '(via Client dashboard)'
      : '(via Operator dashboard)'
    : ride.orderedBy === 'auto'
      ? ''
      : '(via Phone)';

  ride.orderedBy = _.startCase(ride.orderedBy || '') + ' ' + rideOrderedFrom;
  ride.car = _.startCase(ride.car || '');
  ride.carColor = _.startCase(ride.carColor || '');
  ride.rideType = _.startCase(ride.rideType || '');
  ride.serviceType = _.startCase(ride.serviceType || '');
  ride.status = _.startCase(ride.status || '');
  ride.paidStatus = _.startCase(ride.paidStatus || '');
  ride.isDoNotApplySubsidy = ride.isDoNotApplySubsidy ? 'Yes' : 'No';
  ride.isDoNotApplyExpiringCredits = ride.isDoNotApplyExpiringCredits ? 'Yes' : 'No';
  ride.streetAddress = _.startCase(ride.streetAddress || '');
  ride.city = _.startCase(ride.city || '');

  ride.driverChanges = ride.driverChanges ? 'Yes' : '';
  ride.dummyId = ride.dummyId || '';

  ride.isExpandDriverSearch = ride.overrideBadDriver ? 'Yes' : 'No';
  ride.isExpandVehicleSearch = ride.overrideBannedCar || ride.overrideBigCar || ride.overrideSmallCar ? 'Yes' : 'No';

  if (ride.startLocation) {
    ride.startLocationObject = util.getAddressObjectFromString(ride.startLocation) as LocationDataType;
  }

  if (ride.endLocation) {
    ride.endLocationObject = util.getAddressObjectFromString(ride.endLocation) as LocationDataType;
  }

  if (ride.destination) {
    ride.destinationLocationObject = util.getAddressObjectFromString(ride.destination) as LocationDataType;
  }

  if (ride.customerChargeDecline) {
    const list: string[] = [];

    for (const key in ride.customerChargeDecline) {
      list.push(`${key}: ${ride.customerChargeDecline[key]}`);
    }

    ride.customerChargeDecline = {
      list,
    };
  } else {
    ride.customerChargeDecline = {
      list: [],
    };
  }

  if (ride.partnerChargeDecline) {
    const list: string[] = [];

    for (const key in ride.partnerChargeDecline) {
      list.push(`${key}: ${ride.partnerChargeDecline[key]}`);
    }

    ride.partnerChargeDecline = {
      list,
    };
  } else {
    ride.partnerChargeDecline = {
      list: [],
    };
  }

  if (ride.previousDrivers) {
    let htmlString: string = '';
    ride.previousDrivers.map((previousDriver: any) => {
      let content = `<span class="text-red">Driver: ${previousDriver.driverName}</span><br />` || '';
      for (const key in previousDriver) {
        if (key === 'driverName') continue;
        content += `${_.startCase(key)}: ${previousDriver[key]}<br />`;
      }
      htmlString += content ? `<li><div>${content}</div></li>` : '';
    });
    ride.previousDrivers = htmlString ? `<ol class="text-list">${htmlString}</ol>` : '';
  } else {
    ride.previousDrivers = '';
  }

  const regReplace = new RegExp('\n', 'g');
  ride.noteToDriverHtml = ride.noteToDriver ? ride.noteToDriver.replace(regReplace, '<br />') : '';

  ride.intermediateStopsDetail = buildIntermediateStopsDetail(ride.destinations);
  ride.deliveryDetails = buildDeliveryDetailsList(ride.deliveryDetails);

  ride = {
    ...ride,
    ...formatRideDateTimes(ride, timeZone || ride.timeZone),
  };

  return ride;
};

const formatCustomChargeRecordsAndAddToChildrenOfRide = (rides: any, rideCustomChargeRecords: any, isPartner: boolean = false) => {
  for (const record of rideCustomChargeRecords) {
    if (!record.associatedObjectId || record.associatedObjectType !== 'Ride') {
      continue;
    }

    const index = rides.findIndex((ride: any) => ride.requestId === record.associatedObjectId);

    if (index === -1) {
      continue;
    }

    const ride = rides[index];

    const data: any = {
      key: record.id,
      currency: record.currency,
      timeCustomerCharged: util.formatDateTime(record.timeCustomerCharged, dateTimeFormat, ride.timeZone),
      timePartnerCharged: util.formatDateTime(record.timePartnerCharged, dateTimeFormat, ride.timeZone),
      timePartnerRefunded: util.formatDateTime(record.timePartnerRefunded, dateTimeFormat, ride.timeZone),
      timePartnerCreditsRefunded: util.formatDateTime(record.timePartnerCreditsRefunded, dateTimeFormat, ride.timeZone),
      timePartnerCreditsUsed: util.formatDateTime(record.timePartnerCreditsUsed, dateTimeFormat, ride.timeZone),
      timeReceiptReady: util.formatDateTime(record.timeReceiptReady, dateTimeFormat, ride.timeZone),
      timeCustomerRefunded: util.formatDateTime(record.timeCustomerRefunded, dateTimeFormat, ride.timeZone),
      timeCustomerCreditsUsed: util.formatDateTime(record.timeCustomerCreditsUsed, dateTimeFormat, ride.timeZone),
      timeCustomerCreditsRefunded: util.formatDateTime(record.timeCustomerCreditsRefunded, dateTimeFormat, ride.timeZone),
      customerCost: record.customerCost,
      customerChargeId: record.customerChargeId,
      customerRefund: record.customerRefund,
      customerRefundId: record.customerRefundId,
      customerCredits: record.customerCredits,
      customerCreditsRefund: record.customerCreditsRefund,
      partnerCost: record.partnerCost,
      partnerChargeId: record.partnerChargeId,
      partnerRefund: record.partnerRefund,
      partnerRefundId: record.partnerRefundId,
      partnerCredits: record.partnerCredits,
      partnerCreditsRefund: record.partnerCreditsRefund,
      customerChargeDecline: {
        list: [],
      },
      partnerChargeDecline: {
        list: [],
      },
    };

    data.id = ride.id;
    data.name = util.getFullName(ride.firstName, ride.lastName);
    data.requestId = ride.requestId;

    if (isPartner) {
      data.createdAt = ride.createdAt;
      data.riderPhone = ride.riderPhone;
      data.timeZone = ride.timeZone;
      data.customerName = util.getFullName(ride.firstName, ride.lastName);
      data.dob = ride.dob;
      data.status = _.startCase(ride.status || '');

      data.startLocation = ride.startLocation;
      data.destination = ride.destination;
      data.endLocation = ride.endLocation;
    }

    if (ride.children && ride.children.length) {
      ride.children.push(data);
    } else {
      ride.children = [data];
    }
  }
};

const formatRideDataForDownload = (rides: any[]) => {
  for (const ride of rides) {
    for (const field of Object.keys(ride)) {
      if (typeof ride[field] === 'string') {
        ride[field] = ride[field].replace('#', '');
      }
    }
  }
};

const mergeCustomChargeRecordsToRideDownloadData = (rides: any, rideCustomChargeRecords: any) => {
  for (const record of rideCustomChargeRecords) {
    if (!record.associatedObjectId || record.associatedObjectType !== 'Ride') {
      continue;
    }

    const index = rides.findIndex((ride: any) => ride.requestId === record.associatedObjectId);

    if (index === -1) {
      continue;
    }

    const ride = rides[index];

    const data = {
      createdAt: ride.createdAt,
      requestId: ride.requestId,
      firstName: ride.firstName,
      lastName: ride.lastName,
      currency: record.currency,
      timeCustomerCharged: util.formatDateTime(record.timeCustomerCharged, dateTimeFormat, ride.timeZone),
      timePartnerCharged: util.formatDateTime(record.timePartnerCharged, dateTimeFormat, ride.timeZone),
      timePartnerRefunded: util.formatDateTime(record.timePartnerRefunded, dateTimeFormat, ride.timeZone),
      timePartnerCreditsRefunded: util.formatDateTime(record.timePartnerCreditsRefunded, dateTimeFormat, ride.timeZone),
      timePartnerCreditsUsed: util.formatDateTime(record.timePartnerCreditsUsed, dateTimeFormat, ride.timeZone),
      timeReceiptReady: util.formatDateTime(record.timeReceiptReady, dateTimeFormat, ride.timeZone),
      timeCustomerRefunded: util.formatDateTime(record.timeCustomerRefunded, dateTimeFormat, ride.timeZone),
      timeCustomerCreditsUsed: util.formatDateTime(record.timeCustomerCreditsUsed, dateTimeFormat, ride.timeZone),
      timeCustomerCreditsRefunded: util.formatDateTime(record.timeCustomerCreditsRefunded, dateTimeFormat, ride.timeZone),
      customerCost: record.customerCost,
      customerChargeId: record.customerChargeId,
      customerRefund: record.customerRefund,
      customerRefundId: record.customerRefundId,
      customerCredits: record.customerCredits,
      customerCreditsRefund: record.customerCreditsRefund,
      partnerCost: record.partnerCost,
      partnerChargeId: record.partnerChargeId,
      partnerRefund: record.partnerRefund,
      partnerRefundId: record.partnerRefundId,
      partnerCredits: record.partnerCredits,
      partnerCreditsRefund: record.partnerCreditsRefund,
      timeZone: ride.timeZone,
    };

    rides.splice(index + 1, 0, data);
  }
};

const formatScheduledRideData = (scheduledRide: ScheduledRide) => {
  const formattedItem: ExtendedScheduledRide = _.cloneDeep(scheduledRide);
  formattedItem.collapseId = `collapse-${scheduledRide.id}`;
  formattedItem.name = scheduledRide.rideName ?? '';

  const regReplace = new RegExp('\n', 'g');
  formattedItem.noteToDriverView = scheduledRide.noteToDriver ? scheduledRide.noteToDriver.replace(regReplace, '<br />') : '';
  formattedItem.notesForLPView = scheduledRide.notesForLP ? scheduledRide.notesForLP.replace(regReplace, '<br />') : '';

  formattedItem.pickUpTime = scheduledRide.pickupTime?.toUpperCase() ?? '';
  formattedItem.pickUpDate = util.formatDateTime(scheduledRide.pickupDate, 'ddd, MM-DD-YYYY');

  formattedItem.createdAt = util.formatDateTime(scheduledRide.createdAt, dateTimeFormat);
  formattedItem.executedAt = util.formatDateTime(scheduledRide.executedAt, dateTimeFormat);
  formattedItem.completedAt = util.formatDateTime(scheduledRide.completedAt, dateTimeFormat);
  formattedItem.canceledAt = util.formatDateTime(scheduledRide.canceledAt, dateTimeFormat);
  formattedItem.updatedAt = util.formatDateTime(scheduledRide.updatedAt, dateTimeFormat);
  formattedItem.emailedReminderAt = util.formatDateTime(scheduledRide.emailedReminderAt, dateTimeFormat);
  formattedItem.calledReminderAt = util.formatDateTime(scheduledRide.calledReminderAt, dateTimeFormat);
  formattedItem.firstPickup = util.formatDateTime(scheduledRide.firstPickup, dateTimeFormat);

  formattedItem.showCreatedAt = util.formatDateTime(scheduledRide.createdAt, commonDateTimeFormat, scheduledRide.timeZone);
  formattedItem.showExecutedAt = util.formatDateTime(scheduledRide.executedAt, commonDateTimeFormat, scheduledRide.timeZone);
  formattedItem.showCompletedAt = util.formatDateTime(scheduledRide.completedAt, commonDateTimeFormat, scheduledRide.timeZone);
  formattedItem.showCanceledAt = util.formatDateTime(scheduledRide.canceledAt, commonDateTimeFormat, scheduledRide.timeZone);
  formattedItem.showUpdatedAt = util.formatDateTime(scheduledRide.updatedAt, commonDateTimeFormat, scheduledRide.timeZone);
  formattedItem.showEmailedReminderAt = util.formatDateTime(scheduledRide.emailedReminderAt, commonDateTimeFormat, scheduledRide.timeZone);
  formattedItem.showCalledReminderAt = util.formatDateTime(scheduledRide.calledReminderAt, commonDateTimeFormat, scheduledRide.timeZone);
  formattedItem.isExpandDriverSearch = Boolean(scheduledRide.overrideBadDriver);
  formattedItem.isExpandVehicleSearch = Boolean(
    scheduledRide.overrideBannedCar || scheduledRide.overrideBigCar || scheduledRide.overrideSmallCar,
  );

  if (scheduledRide.repeatEveryXWeek) {
    formattedItem.repeatEveryXWeekText = util.getLabelByField(repeatWeeks, scheduledRide.repeatEveryXWeek, 'id');
  }

  formattedItem.skipDates = scheduledRide.skipScheduledRideDates ? scheduledRide.skipScheduledRideDates.join(' , ') : '';
  formattedItem.status = getRideStatus(scheduledRide);

  return formattedItem;
};

const formatRideDateTimes = (ride: any, timeZone: string, format: string = dateTimeFormat) => {
  return {
    createdAt: util.formatDateTime(ride.createdAt, format, timeZone),
    timeCompleted: util.formatDateTime(ride.timeCompleted, format, timeZone),
    timeAccepted: util.formatDateTime(ride.timeAccepted, format, timeZone),
    timeArriving: util.formatDateTime(ride.timeArriving, format, timeZone),
    timeInProgress: util.formatDateTime(ride.timeInProgress, format, timeZone),
    timeCanceled: util.formatDateTime(ride.timeCanceled, format, timeZone),
    timeCustomerCharged: util.formatDateTime(ride.timeCustomerCharged, format, timeZone),
    timePartnerCharged: util.formatDateTime(ride.timePartnerCharged, format, timeZone),
    timePartnerRefunded: util.formatDateTime(ride.timePartnerRefunded, format, timeZone),
    timePartnerCreditsRefunded: util.formatDateTime(ride.timePartnerCreditsRefunded, format, timeZone),
    timePartnerCreditsUsed: util.formatDateTime(ride.timePartnerCreditsUsed, format, timeZone),
    timeReceiptReady: util.formatDateTime(ride.timeReceiptReady, format, timeZone),
    timeCustomerRefunded: util.formatDateTime(ride.timeCustomerRefunded, format, timeZone),
    timeCustomerCreditsUsed: util.formatDateTime(ride.timeCustomerCreditsUsed, format, timeZone),
    timeCustomerCreditsRefunded: util.formatDateTime(ride.timeCustomerCreditsRefunded, format, timeZone),
    dob: ride.dob,
  };
};

/*
  0: active
  1: canceled
  2: finished
*/
const getRideStatus = (scheduledRide: any) => {
  if (!scheduledRide.completedAt && !scheduledRide.canceledAt) {
    return 0;
  } else if (scheduledRide.canceledAt) {
    return 1;
  } else if (scheduledRide.completedAt) {
    return 2;
  }

  return -1;
};

const rideDownloadFields = [
  'createdAt',
  'id',
  'requestId',
  'firstName',
  'lastName',
  'dob',
  'partnerName',
  'riderPhone',
  'customRiderName',
  'timeInProgress',
  'timeCompleted',
  'distance',
  'duration',
  'status',
  'startLocation',
  'destination',
  'endLocation',
  'vendor',
  'partnerRidePurpose',
  'rideType',
  'driverName',
  'car',
  'currency',
  'totalFare',
  'gogoCost',
  'gogoFee',
  'customerCost',
  'customerChargeId',
  'timeCustomerCharged',
  'customerChargeDecline',
  'customerRefund',
  'customerRefundId',
  'timeCustomerRefunded',
  'customerCredits',
  'timeCustomerCreditsUsed',
  'customerCreditsRefund',
  'timeCustomerCreditsRefunded',
  'partnerId',
  'partnerCost',
  'partnerChargeId',
  'timePartnerCharged',
  'partnerChargeDecline',
  'partnerRefund',
  'partnerRefundId',
  'timePartnerRefunded',
  'partnerCredits',
  'timePartnerCreditsUsed',
  'partnerCreditsRefund',
  'timePartnerCreditsRefunded',
  'partnerRides',
  'tip',
  'timeZone',
  'surgeUrl',
  'surgeId',
  'surgeFactor',
  'noteToDriver',
  'reason',
  'partnerNote',
  'appliedSubsidy',
  'streetAddress',
  'city',
];

// convert ride info object to json array ({ id, label, value, isImage}) to show ride info
/* ex:
   if rideInfo is { id: 1, requestId: '123-456', status: 'completed', driverCarImage: 'https://car.image' },
   the result should be
   [
     { id: 'id', label: 'Id', value: 1 },
     { id: 'requestId', label: 'Request Id', value: '123-456 },
     { id: 'status', label: 'Status', value: 'completed' },
     { id: 'driverCarImage', label: 'Driver Car Image', value: 'https://car.image', isImage: true },
   ]
*/
const constructRideTxtArray = (ride: any): RideInfoItem[] => {
  if (!ride || !ride.id) {
    return [];
  }

  const rideFields = [
    'id',
    'requestId',
    'isMultiStop',
    'status',
    'riderPhone',
    'rideType',
    'serviceType',
    'orderedBy',
    'operatorName',
    'startLocation',
    'startLat',
    'startLong',
    'endLocation',
    'endLat',
    'endLong',
    'destination',
    'destinationLat',
    'destinationLng',
    'destinations',
    'endNote',
    'duration',
    'distance',
    'map',
    'gogoMap',
    'driverName',
    'driverPhone',
    'driverPhoto',
    'car',
    'driverCarImage',
    'corpAccountPhone',
    'driverPlate',
    'noteToDriver',
    'createdAt',
    'timeAccepted',
    'timeArriving',
    'timeInProgress',
    'timeCompleted',
    'eta',
    'acceptedEta',
    'surgeFactor',
    'customerCost',
    'customerCredits',
  ] as string[];

  if (ride.timeCanceled) {
    rideFields.splice(4, 0, 'reasonForCanceling');
  }

  if (ride.canceledBy) {
    rideFields.splice(5, 0, 'canceledBy');
  }

  if (ride.scheduledRideId) {
    rideFields.splice(1, 0, 'scheduledRideId');
  }

  const isMultiStop = ride.destinations?.stops?.length > 0;
  const rideInfo: RideInfoItem[] = [];

  rideFields.map((field: string) => {
    if (
      field === 'createdAt' ||
      field === 'timeAccepted' ||
      field === 'timeArriving' ||
      field === 'timeInProgress' ||
      field === 'timeCompleted'
    ) {
      rideInfo.push({
        id: field,
        label: _.startCase(field),
        value: util.formatDateTime(ride[field], 'YYYY-MM-DD hh:mm:ss A', ride.timeZone),
      });
    } else if (field === 'driverCarImage') {
      rideInfo.push({
        id: field,
        label: _.startCase(field),
        value: ride[field] ? ride[field] : '/static/img/no_driver.jpeg',
        type: 'image',
      });
    } else if (field === 'driverPhoto') {
      rideInfo.push({
        id: field,
        label: _.startCase(field),
        value: ride[field] ? ride[field] : '/static/img/no_driver.jpeg',
        type: 'image',
      });
    } else if (field === 'map') {
      rideInfo.push({
        id: 'vendorMap',
        label: 'Vendor Map',
        value: ride[field] ? `<a href="${ride[field]}" target="_blank">Click here for map</a>` : '',
      });
    } else if (field === 'gogoMap') {
      rideInfo.push({
        id: 'gogoMap',
        label: 'GoGo Map',
        value: ride[field] ? `<a href="${ride.gogoMap}" target="_blank">Click here for map</a>` : '',
      });
    } else if (field === 'isMultiStop') {
      rideInfo.push({
        id: field,
        label: 'Multi Stop',
        value: isMultiStop ? 'Yes' : 'No',
      });
    } else if (field === 'destinations') {
      if (isMultiStop) {
        const { list } = buildIntermediateStopsDetail(ride.destinations);
        rideInfo.push({
          id: field,
          label: 'Stops',
          value: list.join('<br />'),
        });
      }
    } else {
      rideInfo.push({ id: field, label: _.startCase(field), value: ride[field] });
    }
  });

  return rideInfo;
};

const convertMilesToTargetUnits = (distance: number, units: MEASUREMENT_SYSTEM) => {
  if (units === MEASUREMENT_SYSTEM.METRIC) {
    return (distance * milesExchange).toFixed(2);
  }

  return distance;
};

const MEASUREMENT_UNIT_MAPPING = new Map([
  [MEASUREMENT_SYSTEM.IMPERIAL, MEASUREMENT_UNITS.MILES],
  [MEASUREMENT_SYSTEM.METRIC, MEASUREMENT_UNITS.KM],
]);

const getDistanceUnit = (units: MEASUREMENT_SYSTEM) => {
  return MEASUREMENT_UNIT_MAPPING.get(units);
};

const getCostPerDistance = (cost: number, units: MEASUREMENT_SYSTEM) => {
  if (units === MEASUREMENT_SYSTEM.METRIC) {
    return (cost / milesExchange).toFixed(2);
  }

  return cost;
};

const updateEstimateLinkCoordinates = (link: string, newLat: number, newLng: number): string => {
  try {
    // The callRideFromEstimateLink is a relative URL path with query params
    // Parse it, update start coordinates, and re-stringify
    const separatorIndex = link.indexOf('?');
    if (separatorIndex === -1) {
      return link;
    }

    const basePath = link.substring(0, separatorIndex);
    const params = new URLSearchParams(link.substring(separatorIndex + 1));

    // Update start coordinates - try common parameter patterns
    if (params.has('startLat')) {
      params.set('startLat', String(newLat));
      params.set('startLng', String(newLng));
    } else if (params.has('startCoors')) {
      // startCoors may be JSON stringified
      params.set('startCoors', JSON.stringify({ lat: newLat, lng: newLng }));
    }

    return `${basePath}?${params.toString()}`;
  } catch {
    return link;
  }
};

// Single source of truth for which amount pair to display, since minAmount/maxAmount collapse for Value60.
const getEffectiveCreditAmounts = (expiringCreditInfo?: RideEstimate['expiringCreditInfo']): { min: number; max: number } => {
  const { minAmount, maxAmount, showMembershipCreditBreakdown, coveredByCreditMin, coveredByCreditMax } = expiringCreditInfo ?? {};
  return {
    min: (showMembershipCreditBreakdown ? coveredByCreditMin : minAmount) ?? 0,
    max: (showMembershipCreditBreakdown ? coveredByCreditMax : maxAmount) ?? 0,
  };
};

const formatCreditRange = (min?: number, max?: number): string => `${(min ?? 0).toFixed(2)}-${(max ?? 0).toFixed(2)}`;

const getFinalEstimateTxt = ({
  rideEstimate,
  planCost,
  offeredPromoCost,
}: {
  rideEstimate: RideEstimate | null;
  planCost?: number | null;
  offeredPromoCost?: number | null;
}) => {
  if (!rideEstimate) {
    return '-';
  }

  let lowEstimate = rideEstimate.lowEstimate;
  let highEstimate = rideEstimate.highEstimate;

  const hasExpiringCreditsAttention = Boolean(rideEstimate.expiringCreditInfo?.minAmount || rideEstimate.expiringCreditInfo?.maxAmount);

  if (hasExpiringCreditsAttention) {
    const { min, max } = getEffectiveCreditAmounts(rideEstimate.expiringCreditInfo);
    lowEstimate -= min;
    highEstimate -= max;
  }

  if (planCost) {
    lowEstimate += planCost;
    highEstimate += planCost;
  }

  if (rideEstimate.partnerSubsidyEstimate) {
    lowEstimate -= rideEstimate.partnerSubsidyEstimate;
    highEstimate -= rideEstimate.partnerSubsidyEstimate;
  }

  if (offeredPromoCost && !hasExpiringCreditsAttention) {
    lowEstimate = Math.max(lowEstimate - offeredPromoCost, 0);
    highEstimate = Math.max(highEstimate - offeredPromoCost, 0);
  }

  lowEstimate = Math.round(Math.max(lowEstimate, 0));
  highEstimate = Math.round(Math.max(highEstimate, 0));

  return formatEstimateRange(lowEstimate, highEstimate);
};
const canceledRideStatuses = ['driver_canceled', 'operator_canceled', 'rider_canceled', 'driver_asked_to_cancel', 'canceled'];
const failedRideStatuses = ['no_drivers_available', 'validation_failed', 'invalid_request_parameters'];
const liveRideStatuses = ['start', 'accepted', 'arriving', 'in_progress'];
const pickupRideStatuses = ['accepted', 'arriving'];
/** Terminal statuses where the ride will no longer change */
const finishedRideStatuses = ['completed', ...canceledRideStatuses, ...failedRideStatuses];

const isLiveRideStatus = (status?: string | null) => !!status && liveRideStatuses.includes(status);
const isPickupRideStatus = (status?: string | null) => !!status && pickupRideStatuses.includes(status);
const isFinishedRideStatus = (status?: string | null) => !!status && finishedRideStatuses.includes(status);
const isCanceledRideStatus = (status?: string | null) => !!status && canceledRideStatuses.includes(status);

export default {
  makeEstimationPostData,
  isVenueEntranceSelected,
  buildDestinationStops,
  getPrefilledLocation,
  parseEstimationRide,
  parseEstimationRideMulti,
  hasEstimateOptions,
  getNoEstimateOptionsMessage,
  getRideSchedulerData,
  formatRideData,
  formatCustomChargeRecordsAndAddToChildrenOfRide,
  mergeCustomChargeRecordsToRideDownloadData,
  rideDownloadFields,
  formatRideDataForDownload,
  constructRideTxtArray,
  formatScheduledRideData,
  convertMilesToTargetUnits,
  getDistanceUnit,
  getCostPerDistance,
  formatRideDateTimes,
  updateEstimateLinkCoordinates,
  getFinalEstimateTxt,
  formatEstimateRange,
  getEffectiveCreditAmounts,
  formatCreditRange,
  prefixNotes,
  isLiveRideStatus,
  isPickupRideStatus,
  isFinishedRideStatus,
  isCanceledRideStatus,
};
